import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import PrivateRoutes from '../components/PrivateRoutes';
import PublicRoutes from '../components/PublicRoutes';
import Loader from '../components/Loader';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorBoundaryModal from '../components/ErrorBoundaryModal';
import LogOutUtil from '../utils/LogOutUtil';
import { logEventOnScreenChange } from '../utils/AnalyticsUtil';
import { logErrorEvent } from '../utils/AnalyticsUtil';
const ForgotPassword = lazy(() => import('pages/ForgotPassword'));
const StyleGuide = lazy(() => import('pages/StyleGuide'));
const Reporting = lazy(() => import('../pages/authenticated/Reporting'));
const ReportingBranch = lazy(() => import('../pages/authenticated/ReportingBranch'));
const EmployeeStats = lazy(() => import('../pages/authenticated/EmployeeStats'));
const Account = lazy(() => import('../pages/authenticated/Account'));
const Support = lazy(() => import('../pages/authenticated/Support'));
const Dashboard = lazy(() => import('../pages/authenticated/Dashboard'));
const Login = lazy(() => import('../pages/Login'));
const Survey = lazy(() => import('../pages/authenticated/Survey'));
const ROICalculator = lazy(() => import('../pages/authenticated/ROICalculator'));
const SignUp = lazy(() => import('../pages/SignUp'));
const SuperAdmin = lazy(() => import('../pages/authenticated/SuperAdmin/index.jsx'));
const PageNotFound = lazy(() => import('../pages/PageNotFound'));
const VerificationScreen = lazy(() => import('../pages/Login/components/VerificationScreen'));
const SignupErrorComponent = lazy(() => import('../pages/SignUp/components/SignupErrorComponent'));
const SignupMobileComponent = lazy(() => import('../pages/SignUp/components/SignupMobileComponent'));
const SignupOtpScreen = lazy(() => import('../pages/SignUp/components/SignupOtpScreen'));
const SignupPassword = lazy(() => import('../pages/SignUp/components/SignupPassword'));
const EmailVerification = lazy(() => import('../pages/ForgotPassword/components/EmailVerification'));
const ResetPassword = lazy(() => import('../pages/ForgotPassword/components/ResetPassword'));
const ResetPasswordSuccess = lazy(
  () => import('../pages/ForgotPassword/components/ResetPasswordSuccess'),
);
const RequestSignupLink = lazy(() => import('../pages/SignUp/components/RequestSignupLink'));
const RequestSendComponent = lazy(() => import('../pages/SignUp/components/RequestSendComponent'));
const RequestSelfSignupLink = lazy(() => import('../pages/SelfSignUp'));
const VerifyEmailComponent = lazy(() => import('../pages/SelfSignUp/components/VerifyEmailComponent'));
const VerifyCodeComponent = lazy(() => import('../pages/SelfSignUp/components/VerifyCodeComponent'));
const CreatePassword = lazy(() => import('../pages/SelfSignUp/components/CreatePassword'));
const AccountSetUp = lazy(() => import('../pages/SelfSignUp/components/AccountSetUp'));
const PayrollProvider = lazy(() => import('../pages/authenticated/PayrollProvider'));
const PayrollProviderIntegration = lazy(
  () => import('../pages/authenticated/PayrollProvider/components/PayrollProviderIntegration'),
);
const Invoices = lazy(() => import('../pages/authenticated/Invoices'));
const RewardConfiguration = lazy(() => import('../pages/authenticated/Account/RewardConfiguration'));
const CareHeroesBilling = lazy(() => import('../pages/authenticated/Account/CareHeroesBilling'));
const CustomerSupport = lazy(() => import('../pages/authenticated/Account/CustomerSupport'));
const Profile = lazy(() => import('../pages/authenticated/Account/Profile'));

const Routing = () => {
  const { logOutUser } = LogOutUtil();
  const location = useLocation();
  const isLoading = false;
  const ErrorFallback = ({ error, resetErrorBoundary }) => {
    logErrorEvent(error.message, location?.pathname);
    return <ErrorBoundaryModal error={error} resetErrorBoundary={resetErrorBoundary} />;
  };

  const logOutCallback = () => {
    logOutUser();
  };

  useEffect(() => {
    logEventOnScreenChange(location?.pathname);
  }, [location]);

  return !isLoading ? (
    <div className="app-container d-flex col-12">
      <div className="position-relative w-100">
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() => {
            logOutCallback();
          }}
        >
          <Suspense fallback={<Loader fullScreen={true} />}>
            <Routes>
              <Route element={<PrivateRoutes />}>
                <Route exact path="/" element={<Dashboard />} />
                <Route exact path="*" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/reporting" element={<Reporting />} />
                <Route path="/reporting/facility" element={<Reporting />} />
                <Route path="/reporting/agency" element={<ReportingBranch />} />
                <Route path="/reporting/branch" element={<ReportingBranch />} />
                <Route path="/employee-stats" element={<EmployeeStats />} />
                <Route path="/account" element={<Account />}>
                  <Route path="/account/profile" element={<Profile />} />
                  <Route
                    path="/account/reward-configuration/:paymentStatus?"
                    element={<RewardConfiguration />}
                  />
                  <Route
                    path="/account/care-heroes-bank/:paymentStatus?"
                    element={<CareHeroesBilling />}
                  />
                  <Route path="/account/customer-support" element={<CustomerSupport />} />
                </Route>
                <Route path="/support" element={<Support />} />
                <Route
                  path="/support-failed-redemptions"
                  element={<Support defaultTab="failed_redemptions" />}
                />
                <Route path="/support-account-merge" element={<Support defaultTab="wallet_merge" />} />
                <Route
                  path="/support-change-password"
                  element={<Support defaultTab="change_password" />}
                />
                <Route
                  path="/support-manual-redemption"
                  element={<Support defaultTab="manual_redemption" />}
                />
                <Route
                  path="/support-change-phone-number"
                  element={<Support defaultTab="change_phone_number" />}
                />
                <Route path="/invoices/:paymentStatus?" element={<Invoices />} />
                <Route path="/survey" element={<Survey />} />
                <Route path="/roi-calculator" element={<ROICalculator />} />
                <Route path="/super-admin" element={<SuperAdmin />} />
                <Route path="/connect-to-payroll-provider" element={<PayrollProvider />} />
                <Route
                  path="/payroll-provider-connection-success"
                  element={<PayrollProviderIntegration />}
                />
                <Route exact path="/styleguide" element={<StyleGuide />} />
              </Route>
              <Route element={<PublicRoutes />}>
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/email-verification" element={<EmailVerification />} />
                <Route path="/reset-password-success" element={<ResetPasswordSuccess />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/login" element={<Login />} />
                <Route path="/login-otp" element={<VerificationScreen />} />
                <Route path="/email-verify/:token" element={<SignUp />} />
                <Route path="/signup-error" element={<SignupErrorComponent />} />
                <Route path="/signup-mobile" element={<SignupMobileComponent />} />
                <Route path="/signup-otp" element={<SignupOtpScreen />} />
                <Route path="/create-password" element={<SignupPassword />} />
                <Route path="/signup-link" element={<RequestSignupLink />} />
                <Route path="/signup-request-send" element={<RequestSendComponent />} />
                <Route path="*" element={<PageNotFound />} />
                <Route path="/request-self-signup-link" element={<RequestSelfSignupLink />} />
                <Route path="/verify-signup-email" element={<VerifyEmailComponent />} />
                <Route path="/verify-signup-otp" element={<VerifyCodeComponent />} />
                <Route path="/create-signup-password/:token?" element={<CreatePassword />} />
                <Route path="/create-account" element={<AccountSetUp />} />
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  ) : (
    <Loader fullScreen={true} />
  );
};

export default Routing;
